import React, { FC, useMemo } from 'react';
import { AppManagerProps } from '../../types/appManager';
import { ExperimentsProvider } from '@wix/wix-experiments-react';
import { EditorPlatformBIProvider } from '../providers/EditorPlatformBIProvider';
import { EditorPlatformFedopsProvider } from '../providers/EditorPlatformFedopsProvider';
import { EditorPlatformI18nProvider } from '../providers/EditorPlatformI18nProvider';
import { AppManagerContainer } from './AppManagerContainer/AppManagerContainer';
import { getAppManagerPrivateAPI } from './appManagerAPI';
import { AppManagerDataProvider } from './providers/AppManagerDataProvider';
import { WixDesignSystemProvider } from '@wix/design-system';
import { AppManagerErrorBoundary } from './AppManagerErrorBoundary';
import { PLATFORM_BI_ENDPOINT } from './constants/biEvents';

export const AppManager: FC<AppManagerProps> = ({
  appsList,
  pendingAppDefIds,
  userPermissions,
  allAvailableToUpdateApps,
  apiDependencies,
  onPanelTitleReady,
  selectedAppId,
  selectedAppFromUrl,
  origin,
  locale,
  experiments,
}) => {
  const api = useMemo(
    () =>
      getAppManagerPrivateAPI(
        appsList,
        pendingAppDefIds,
        userPermissions,
        allAvailableToUpdateApps,
        apiDependencies,
        experiments,
      ),
    [
      appsList,
      pendingAppDefIds,
      userPermissions,
      allAvailableToUpdateApps,
      apiDependencies,
      experiments,
    ],
  );

  return (
    <WixDesignSystemProvider features={{ newColorsBranding: true }}>
      <EditorPlatformI18nProvider locale={locale}>
        <EditorPlatformBIProvider endpoint={PLATFORM_BI_ENDPOINT}>
          <EditorPlatformFedopsProvider>
            <ExperimentsProvider experiments={experiments}>
              <AppManagerErrorBoundary>
                <AppManagerDataProvider api={api} locale={locale}>
                  <AppManagerContainer
                    data-aid="app-manager-container"
                    selectedAppId={selectedAppId}
                    selectedAppFromUrl={selectedAppFromUrl}
                    origin={origin}
                    onPanelTitleReady={onPanelTitleReady}
                  />
                </AppManagerDataProvider>
              </AppManagerErrorBoundary>
            </ExperimentsProvider>
          </EditorPlatformFedopsProvider>
        </EditorPlatformBIProvider>
      </EditorPlatformI18nProvider>
    </WixDesignSystemProvider>
  );
};
